class ApiMethods {
    static async requestWithRetry(fn, { maxRetries = 3, delay = 1000 } = {}) {
        let attempt = 0;
        while (attempt < maxRetries) {
            try {
                return await fn();
            } catch (error) {
                attempt += 1;
                if (attempt >= maxRetries) {
                    throw error;
                }

                const backoff = delay * Math.pow(2, attempt - 1);
                await new Promise(resolve => setTimeout(resolve, backoff));
            }
        }
    }

    static async apiRequest(method, url, accessToken, body = null) {
        const options = {method: method, headers: {Authorization: `Bearer ${accessToken}`}};
        if (body) {
            options.headers['Content-Type'] = 'application/json';
            options.body = JSON.stringify(body);
        }

        const response = await fetch(
            `${process.env.REACT_APP_KEYNOME_API_URL_BASE}/${url}`,
            options
        );

        if (!response.ok) {
            const errorMessage = await response.text();
            const error = new Error(`Error: ${response.status} ${response.statusText} ${errorMessage}`);
            error.statusCode = response.status;
            throw error;
        }

        // Handle 204 No Content responses (e.g., from /analyze endpoint)
        if (response.status === 204) {
            return null;
        }

        const jsonResponse = await response.json()
        return jsonResponse.data;
    }

    static async apiRequestRaw(method, url, accessToken, body = null) {
        const options = { method, headers: { Authorization: `Bearer ${accessToken}` } };
        if (body) {
            options.headers['Content-Type'] = 'application/json';
            options.body = JSON.stringify(body);
        }

        const response = await fetch(
            `${process.env.REACT_APP_KEYNOME_API_URL_BASE}/${url}`,
            options
        );

        const status = response.status;
        if (status === 204) {
            return { data: null, status };
        }

        let data = null;
        try {
            const jsonResponse = await response.json();
            data = jsonResponse?.data ?? jsonResponse;
        } catch (error) {
            data = null;
        }

        return { data, status };
    }

    static get(url, accessToken) {
        return this.apiRequest('GET', url, accessToken);
    }

    static post(url, accessToken, body) {
        return this.apiRequest('POST', url, accessToken, body);
    }

    static put(url, accessToken, body) {
        return this.apiRequest('PUT', url, accessToken, body);
    }

    static async uploadToSignedUrl(signedUrl, blob, contentType = 'application/gzip', onProgress) {
        return this.requestWithRetry(async () => {
            await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open('PUT', signedUrl);
                xhr.setRequestHeader('Content-Type', contentType);

                if (typeof onProgress === 'function') {
                    xhr.upload.onprogress = (event) => {
                        if (!event.lengthComputable || event.total <= 0) {
                            return;
                        }
                        onProgress({ loaded: event.loaded, total: event.total });
                    };
                }

                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        resolve();
                        return;
                    }
                    reject(new Error(`Upload failed: ${xhr.status}`));
                };
                xhr.onerror = () => reject(new Error('Upload failed: Network error'));
                xhr.send(blob);
            });
        });
    }
}

export default ApiMethods;